import React from 'react';

import axios from 'axios';
import { useQuery } from 'react-query';

import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import userDataStorage from 'storages/userDataStorage';

import { canAccessOwnerAssetRopaPage } from '../_shared/assetRopaReviewAccess';
import OwnerAssetRopaWorkspacePage from '../OwnerAssetRopaWorkspacePage/OwnerAssetRopaWorkspacePage';
import AssetRopaReviewPage from '../ProcessingActivitiesAssetReviewsPage/AssetRopaReviewPage';

/**
 * Asset Owner ROPA entry:
 * - Active review → owner review UI fed by asset-ropa/mine* (not admin assets list)
 * - Otherwise → owner workspace (also mine-scoped)
 */
const OwnerAssetRopaPage: React.FC = () => {
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccessPage = canAccessOwnerAssetRopaPage(userData);

  const mineBootstrapQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE, organizationId, 'owner-entry-bootstrap'],
    async () => {
      const { data } = await assetRopaApi.getMine(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && canAccessPage),
      retry: false,
    },
  );

  if (!canAccessPage) {
    return <AssetRopaReviewPage variant='owner' />;
  }

  if (mineBootstrapQuery.isLoading) {
    return (
      <ErrorBoundary>
        <Loader inner />
      </ErrorBoundary>
    );
  }

  const bootstrapForbidden =
    mineBootstrapQuery.isError && axios.isAxiosError(mineBootstrapQuery.error) && mineBootstrapQuery.error.response?.status === 403;

  if (bootstrapForbidden) {
    return <AssetRopaReviewPage variant='owner' />;
  }

  if (mineBootstrapQuery.data?.activeReview) {
    return <AssetRopaReviewPage variant='owner' />;
  }

  return <OwnerAssetRopaWorkspacePage />;
};

export default OwnerAssetRopaPage;
